import React from 'react';
import QRCodeStyling from 'qr-code-styling';

interface RoundedQRCodeProps {
    value: string;
    size?: number;
    background?: string;
    foreground?: string;
    cornerRadius?: number; // 0-1 for dots radius relative to module size
}

/**
 * RoundedQRCode renders a rounded-module QR using qr-code-styling (canvas under the hood)
 * Falls back to react-qr-code if canvas is not supported.
 */
export default function RoundedQRCode({ value, size = 200, background = '#111827', foreground = '#e5e7eb', cornerRadius = 0 }: RoundedQRCodeProps) {
    const ref = React.useRef<HTMLDivElement | null>(null);
    const qrRef = React.useRef<QRCodeStyling | null>(null);

    React.useEffect(() => {
        try {
            if (!qrRef.current) {
                qrRef.current = new QRCodeStyling({
                    width: size,
                    height: size,
                    type: 'canvas',
                    data: value,
                    margin: 2,
                    qrOptions: {
                        errorCorrectionLevel: 'Q',
                    },
                    dotsOptions: {
                        type: 'square',
                        color: foreground,
                        // square modules
                    } as any,
                    cornersSquareOptions: { type: 'square', color: foreground } as any,
                    cornersDotOptions: { type: 'dot', color: foreground } as any,
                    backgroundOptions: { color: background },
                });
            }
            const el = ref.current;
            if (el) {
                el.innerHTML = '';
                qrRef.current.update({
                    data: value,
                    width: size,
                    height: size,
                    backgroundOptions: { color: background },
                    dotsOptions: { type: 'square', color: foreground } as any,
                    cornersSquareOptions: { type: 'square', color: foreground } as any,
                    cornersDotOptions: { type: 'dot', color: foreground } as any,
                });
                qrRef.current.append(el);
            }
        } catch {
            // ignore, component will render nothing on failure
        }
    }, [value, size, background, foreground, cornerRadius]);

    return <div ref={ref} style={{ width: size, height: size }} />;
}
